import { Phone, Mail, MapPin, Clock, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { IMAGES } from "@/lib/images";
import { PageHero } from "@/components/PageBlocks";
import { Reveal } from "@/components/Reveal";
import { useSeo } from "@/hooks/useSeo";
import { track } from "@/lib/analytics";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const PATHS = [
  {
    overline: "Planning a trip?",
    title: "Start Your Journey",
    desc: "Tell us about the trip you're imagining — a Travel Experience Specialist will help you explore the right options. This is a planning request, not a booking.",
    cta: "Start Your Journey",
    to: "/start-your-journey",
    testid: "contact-path-plan",
  },
  {
    overline: "Already know what you want?",
    title: "Ready to Book",
    desc: "Send a reservation request with the details you have. We'll confirm availability and next steps — no payment is collected online.",
    cta: "Ready to Book",
    to: "/ReadyToBook",
    testid: "contact-path-book",
  },
];

export default function Contact() {
  useSeo({ title: "Contact Inforno Travel", description: "Reach Inforno Travel at (202) 816-5560 or info@infornotravel.com. Washington, DC Metropolitan Area. Monday–Friday, 8:30 AM–5:30 PM ET." });
  return (
    <div className="bg-noir min-h-screen">
      <Navbar />
      <PageHero overline="Contact" title="Let's Plan What's Next." image={IMAGES.yacht}
        sub="Have a trip in mind or just getting started? Reach out and one of our Travel Experience Specialists will help." />

      {/* Three-path funnel: pick the path that fits */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-noir">
        <div className="max-w-[1500px] mx-auto">
          <Reveal>
            <p className="font-body text-[11px] uppercase tracking-[0.4em] text-gold mb-5">Choose Your Path</p>
            <h2 className="font-serif text-3xl md:text-5xl font-light tracking-tight leading-[0.98]">The right next step, <span className="italic text-white/60">whichever stage you're at.</span></h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {PATHS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.06} className="h-full">
                <div data-testid={p.testid} className="bg-noir p-8 md:p-10 h-full flex flex-col">
                  <p className="font-body text-[11px] uppercase tracking-[0.3em] text-gold mb-4">{p.overline}</p>
                  <h3 className="font-serif text-2xl md:text-3xl font-light">{p.title}</h3>
                  <p className="font-body text-white/55 text-sm leading-relaxed font-light mt-4 flex-1">{p.desc}</p>
                  <Link to={p.to} data-testid={`${p.testid}-cta`}
                    className="mt-8 inline-flex items-center gap-3 border border-gold text-gold px-7 py-3.5 font-body text-xs uppercase tracking-[0.22em] hover:bg-gold hover:text-black transition-colors duration-300 self-start">
                    {p.cta} <ArrowRight size={14} />
                  </Link>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.12} className="h-full">
              <div data-testid="contact-path-question" className="bg-noir p-8 md:p-10 h-full flex flex-col">
                <p className="font-body text-[11px] uppercase tracking-[0.3em] text-gold mb-4">Have a question?</p>
                <h3 className="font-serif text-2xl md:text-3xl font-light">Contact Inforno Travel</h3>
                <p className="font-body text-white/55 text-sm leading-relaxed font-light mt-4 flex-1">We're happy to help directly by email or phone.</p>
                <div className="mt-8 space-y-4">
                  <a href="mailto:info@infornotravel.com" data-testid="contact-path-email" onClick={() => track("contact_email_click", { source_page: "Contact" })}
                    className="flex items-center gap-3 text-white/75 hover:text-gold transition-colors font-body text-sm">
                    <Mail size={16} className="text-gold shrink-0" /> info@infornotravel.com
                  </a>
                  <a href="tel:+12028165560" data-testid="contact-path-phone" onClick={() => track("contact_phone_click", { source_page: "Contact" })}
                    className="flex items-center gap-3 text-white/75 hover:text-gold transition-colors font-body text-sm">
                    <Phone size={16} className="text-gold shrink-0" /> (202) 816-5560
                  </a>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Direct contact details */}
      <section className="py-24 md:py-28 px-6 md:px-12 bg-[#0a0a0a] border-y border-white/10">
        <div className="max-w-[1500px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-6 space-y-8">
            <Reveal>
              <a href="tel:+12028165560" data-testid="contact-phone" onClick={() => track("contact_phone_click", { source_page: "Contact" })} className="flex items-start gap-4 group">
                <Phone size={20} className="text-gold mt-1" strokeWidth={1.4} />
                <span><span className="block font-body text-[11px] uppercase tracking-[0.3em] text-white/40 mb-1">Phone</span><span className="font-serif text-2xl font-light group-hover:text-gold transition-colors">(202) 816-5560</span></span>
              </a>
            </Reveal>
            <Reveal delay={0.05}>
              <a href="mailto:info@infornotravel.com" data-testid="contact-email" onClick={() => track("contact_email_click", { source_page: "Contact" })} className="flex items-start gap-4 group">
                <Mail size={20} className="text-gold mt-1" strokeWidth={1.4} />
                <span><span className="block font-body text-[11px] uppercase tracking-[0.3em] text-white/40 mb-1">Email</span><span className="font-serif text-2xl font-light group-hover:text-gold transition-colors break-all">info@infornotravel.com</span></span>
              </a>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="flex items-start gap-4">
                <MapPin size={20} className="text-gold mt-1" strokeWidth={1.4} />
                <span><span className="block font-body text-[11px] uppercase tracking-[0.3em] text-white/40 mb-1">Mailing Address</span>
                  <span className="font-body text-white/75 text-base leading-relaxed">Inforno Travel<br />2 Massachusetts Avenue NE, Suite #75464<br />Washington, DC 20013</span></span>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="flex items-start gap-4">
                <Clock size={20} className="text-gold mt-1" strokeWidth={1.4} />
                <span><span className="block font-body text-[11px] uppercase tracking-[0.3em] text-white/40 mb-1">Hours</span>
                  <span className="font-body text-white/75 text-base leading-relaxed">Monday–Friday<br />8:30 AM – 5:30 PM ET</span></span>
              </div>
            </Reveal>
          </div>

          <div className="md:col-span-5 md:col-start-8">
            <Reveal delay={0.1}>
              <div className="border border-white/10 p-10 bg-noir">
                <p className="font-body text-[11px] uppercase tracking-[0.4em] text-gold mb-5">Start Here</p>
                <h2 className="font-serif text-3xl md:text-4xl font-light leading-tight">Tell us about the trip you're planning.</h2>
                <p className="font-body text-white/55 mt-5 text-sm leading-relaxed font-light">The fastest way to get started is to share a few details. We'll review them and reach out to help you explore your options.</p>
                <Link to="/start-your-journey" className="mt-8 inline-flex items-center gap-3 border border-gold bg-gold text-black px-8 py-4 font-body text-xs uppercase tracking-[0.22em] hover:bg-transparent hover:text-gold transition-colors duration-300">
                  Start Your Journey <ArrowRight size={15} />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
